import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { transferApi } from '@/api/transferApi';
import { useUIStore } from '@/store/uiStore';
import { QK } from './queryKeys';
import type { CreateTransferRequest, CreateOfferRequest, TransferFilter } from '@/types';
import { getApiErrorMessage } from '@/utils';

export function useGuestTransfers(filters: TransferFilter) {
  return useQuery({
    queryKey: QK.GUEST_TRANSFERS_LIST(filters),
    queryFn: () => transferApi.getListGuest(filters),
    placeholderData: (prev) => prev,
    staleTime: 1000 * 60 * 2,
  });
}

export function useTransfers(filters: TransferFilter) {
  return useQuery({
    queryKey: QK.TRANSFERS_LIST(filters),
    queryFn: () => transferApi.getList(filters),
    placeholderData: (prev) => prev,
    staleTime: 1000 * 60 * 2,
  });
}

export function useTransfer(id: string) {
  return useQuery({
    queryKey: QK.TRANSFER(id),
    queryFn: () => transferApi.getById(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
  });
}

export function useTransferOffers(id: string) {
  return useQuery({
    queryKey: QK.TRANSFER_OFFERS,
    queryFn: () => transferApi.getOffers(id),
    enabled: !!id,
  });
}

export function useMyTransfers() {
  return useQuery({
    queryKey: QK.TRANSFERS_MINE,
    queryFn: transferApi.getMine,
  });
}

export function useCreateTransfer() {
  const qc = useQueryClient();
  const { addToast } = useUIStore();

  return useMutation({
    mutationFn: (data: CreateTransferRequest) => transferApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transfers'] });
      addToast('success', 'Đăng tin sang nhượng thành công!');
    },
    onError: (err: any) => addToast('error', getApiErrorMessage(err, 'Đăng tin thất bại')),
  });
}

export function useUpdateTransfer() {
  const qc = useQueryClient();
  const { addToast } = useUIStore();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateTransferRequest> }) => transferApi.update(id, data),
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['transfers'] });
      qc.invalidateQueries({ queryKey: QK.TRANSFER(variables.id) });
      addToast('success', 'Cập nhật tin thành công!');
    },
    onError: (err: any) => addToast('error', getApiErrorMessage(err, 'Cập nhật tin thất bại')),
  });
}

export function useDeleteTransfer() {
  const qc = useQueryClient();
  const { addToast } = useUIStore();

  return useMutation({
    mutationFn: (id: string) => transferApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transfers'] });
      addToast('success', 'Đã xóa tin');
    },
    onError: (err: any) => addToast('error', getApiErrorMessage(err, 'Xóa tin thất bại')),
  });
}

export function useSendOffer() {
  const qc = useQueryClient();
  const { addToast } = useUIStore();

  return useMutation({
    mutationFn: (data: CreateOfferRequest) => transferApi.sendOffer(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.TRANSFER_OFFERS });
      // qc.invalidateQueries({ queryKey: QK.TRANSFER(id) });
      addToast('success', 'Đã gửi đề nghị thành công!');
    },
    onError: (err: any) => addToast('error', getApiErrorMessage(err, 'Gửi đề nghị thất bại')),
  });
}
